import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from './Container';
import { Form, Formik } from 'formik';
import { Button, Spinner } from '@vwfs-bronson/bronson-react';
import { DATE_FORMAT, EarlySettlementData } from 'cpie-common';
import { formatCpDate } from '@cp-shared-15/common-utilities';
import * as Yup from 'yup';
import { Notification, NotificationStatus, ValidatedDate } from '@cp-shared-15/frontend-ui';
import { CpDataApi } from '../../../cp-xhr';
import { useDealerDetails } from '../../contracts/dealer-details/useDealerDetails';
import { DealerCallbackCreationResponse } from './types';

const initialValues = {
    callbackDate: '',
};

type FormValues = {
    callbackDate: string;
};

const minDate = formatCpDate()
    .add(1, 'days')
    .format(DATE_FORMAT);

const minDateValue = formatCpDate(minDate, DATE_FORMAT)
    .toMoment()
    .toDate();

const maxDate = formatCpDate()
    .add(1, 'month')
    .format(DATE_FORMAT);

const maxDateValue = formatCpDate(maxDate, DATE_FORMAT)
    .toMoment()
    .toDate();

export const StateRequestCallback = ({
    contractNumber,
    encryptedContractNumber,
    earlySettlementData,
    onStepComplete,
}: {
    contractNumber: string;
    encryptedContractNumber: string;
    earlySettlementData: EarlySettlementData;
    onStepComplete: () => void;
}) => {
    const { t, i18n } = useTranslation('early-settlement');
    const [state, setState] = useState<'' | 'loading' | 'error'>('');
    const { data: dealerDetails, isLoading: dealerDetailsIsLoading } = useDealerDetails(contractNumber);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                callbackDate: Yup.date()
                    .transform(function(value, originalValue) {
                        if (!originalValue) return value;
                        const date = formatCpDate(originalValue, DATE_FORMAT).toMoment();
                        return date.toDate();
                    })
                    .typeError(t('dealer-callback.request-callback-section.request.date-invalid1'))
                    .required(t('dealer-callback.request-callback-section.request.date-required'))
                    .min(minDateValue, t('dealer-callback.request-callback-section.request.date-invalid2'))
                    .max(
                        maxDateValue,
                        t('dealer-callback.request-callback-section.request.date-invalid3', { maxDate }),
                    ),
            }),
        [t],
    );

    const onSubmit = useCallback(
        (values: FormValues): void => {
            if (state === 'loading') {
                return;
            }
            setState('loading');

            const requestBody = {
                customerPreferredCallbackDate: formatCpDate(values.callbackDate, DATE_FORMAT).format('YYYY-MM-DD'),
                interestSaving: earlySettlementData.interestSaving,
                arrears: earlySettlementData.arrears,
                totalSettlementAmount: earlySettlementData.totalSettlementAmount,
                settlementExpiryDate: earlySettlementData.enquiryValidUntil,
            };

            CpDataApi.post<DealerCallbackCreationResponse>(
                `/early-settlement/${encryptedContractNumber}/dealer-callback`,
                requestBody,
            )
                .then(res => {
                    if (res.data.status === 'success') {
                        onStepComplete();
                        setState('');
                        return;
                    }

                    setState('error');
                })
                .catch(() => {
                    setState('error');
                });
        },
        [encryptedContractNumber, earlySettlementData, state],
    );

    if (dealerDetailsIsLoading) {
        return <Spinner center />;
    }

    return (
        <>
            {state === 'error' && (
                <Notification
                    testId={'dealerCallbackErrorNotification'}
                    status={NotificationStatus.error}
                    className={'u-mb'}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('dealer-callback.request-callback-section.request.error'),
                        }}
                    />
                </Notification>
            )}

            <Container title={t('dealer-callback.request-callback-section.request.title')}>
                <p className="u-mb-none">
                    {t('dealer-callback.request-callback-section.request.text-dealership-name', {
                        name: dealerDetails?.name || '-',
                    })}
                </p>
                <p className="u-mb-small">{t('dealer-callback.request-callback-section.request.text-call-request')}</p>

                <Formik<FormValues>
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    validationSchema={validationSchema}
                >
                    {(): JSX.Element => (
                        <Form>
                            <div className="u-mb-small">
                                <label htmlFor="callbackDate" className="u-text-white">
                                    <small>
                                        {t('dealer-callback.request-callback-section.request.datepicker-label')}
                                    </small>
                                </label>
                                <ValidatedDate
                                    id="callbackDate"
                                    label={''}
                                    language={i18n.languages?.[0] || 'en'}
                                    outputDateFormat="d/m/Y"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    excludeWeekend={true}
                                    name="callbackDate"
                                />
                            </div>
                            <Button type="submit" secondary disabled={state === 'loading'}>
                                {state === 'loading' ? (
                                    <Spinner small />
                                ) : (
                                    t('dealer-callback.request-callback-section.request.button')
                                )}
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Container>
        </>
    );
};
